import {Injectable, Injector} from '@angular/core';
import {CommodityModel} from '@shared/models/commodity.model';
import {FileModel} from '../models/enums.model';
import {RequestOptions, RequestOptionsArgs, Response, URLSearchParams} from '@angular/http';
import {Utilities} from '@shared/utilities/utilities.model';
import {InnerMessageAuthorityMessageCode} from './inner-message-authority-utilities';
import {MessageComponentType, MessageDetail, MessageType} from '@shared/service/message-manager/message.model';
import {MessageManagerUtilities} from '@shared/service/message-manager/message-manager-utilities';
import {AuthorityBaseService} from './authority-base.service';

@Injectable({
	providedIn: 'root'
})
export class AttachmentsService extends AuthorityBaseService {

	static getAttachmentsRequestOptions(
		commodities: CommodityModel[],
		params: { key: string, value: string }[]
	): RequestOptionsArgs {
		const requestOptions: RequestOptionsArgs = new RequestOptions();
		if (params && params.length) {
			requestOptions.params = new URLSearchParams();
			params.forEach(p => (<URLSearchParams>requestOptions.params).append(p.key, p.value));
		}
		return Utilities.setOptionsWithNemoCommodityID(commodities.map(c => c.id), requestOptions);
	}

	constructor(protected injector: Injector) {
		super(injector);
	}

	addAttachment(
		url: string,
		commodities: CommodityModel[],
		file: FileModel,
		additionalRequestBody: any,
		onSuccess: (message: MessageDetail) => void,
		onFailure: (message: MessageDetail) => void,
		params?: { key: string, value: string }[],
	): void {
		const options: RequestOptionsArgs = AttachmentsService.getAttachmentsRequestOptions(commodities, params);
		const body: any = additionalRequestBody ? {attachment: file, redelivery_data: additionalRequestBody} : file;
		this.httpAuthService.post(url, body, this.redirectUrl, options).subscribe(
			res => this.handleAttachmentsResponse(res, AttachmentRequestMethod.POST, onSuccess, onFailure),
			err => onFailure(MessageManagerUtilities.getMessageDetailsFromResponse(err))
		);
	}

	addCloseAttachment(
		url: string,
		commodities: CommodityModel[],
		file: FileModel,
		additionalRequestBody: any,
		onSuccess: (message: MessageDetail) => void,
		onFailure: (message: MessageDetail) => void,
		params?: { key: string, value: string }[],
	): void {
		const options: RequestOptionsArgs = AttachmentsService.getAttachmentsRequestOptions(commodities, params);
		const body: any = additionalRequestBody ? {attachment: file, redelivery_data: additionalRequestBody} : file;
		this.httpAuthService.patch(url, body, this.redirectUrl, options).subscribe(
			response => {
				if (response.ok) {
					onSuccess({
						code: InnerMessageAuthorityMessageCode.AUTHORITY_SUCCESS_ATTACHMENTS_ADDED,
						type: MessageType.TYPE_SUCCESS,
						componentType: MessageComponentType.FLASH_MESSAGE
					});
				} else {
					onFailure(MessageManagerUtilities.getMessageDetailsFromResponse(response));
				}
			},
			error => onFailure(MessageManagerUtilities.getMessageDetailsFromResponse(error))
		);
	}

	addAttachments(
		url: string,
		commodities: CommodityModel[],
		files: FileModel[],
		onSuccess: (message: MessageDetail) => void,
		onFailure: (message: MessageDetail) => void,
		params?: { key: string, value: string }[]
	): void {
		const options: RequestOptionsArgs = AttachmentsService.getAttachmentsRequestOptions(commodities, params);
		this.httpAuthService.post(url, {attachments: files}, this.redirectUrl, options)
			.subscribe(
				res => this.handleAttachmentsResponse(res, AttachmentRequestMethod.POST, onSuccess, onFailure),
				err => onFailure(MessageManagerUtilities.getMessageDetailsFromResponse(err))
			);
	}

	removeAttachments(
		url: string,
		commodities: CommodityModel[],
		attachmentIds: string[],
		onSuccess: (message: MessageDetail) => void,
		onFailure: (message: MessageDetail) => void,
		params?: { key: string, value: string }[]
	): void {
		const options: RequestOptionsArgs = AttachmentsService.getAttachmentsRequestOptions(commodities, params);
		this.httpAuthService.delete(url, this.redirectUrl, options)
			.subscribe(
				res => this.handleAttachmentsResponse(res, AttachmentRequestMethod.DELETE, onSuccess, onFailure),
				err => onFailure(MessageManagerUtilities.getMessageDetailsFromResponse(err))
			);
	}

	private handleAttachmentsResponse(
		response: Response,
		requestMethod: AttachmentRequestMethod,
		onSuccess: (message: MessageDetail) => void,
		onFailure: (message: MessageDetail) => void
	): void {
		const isPost: boolean = requestMethod === AttachmentRequestMethod.POST;
		if (response.ok) {
			const attachmentsWithError: AttachmentResponse[] = ((<any>response.json()).attachments || []).filter(a => a.status === (isPost ? AttachmentStatus.ERROR : AttachmentStatus.UNABLE_TO_DELETED));
			if (attachmentsWithError.length) {
				onFailure({
					type: MessageType.TYPE_ERROR,
					code: isPost ? InnerMessageAuthorityMessageCode.AUTHORITY_ERROR_UPLOADING_ATTACHMENTS : InnerMessageAuthorityMessageCode.AUTHORITY_ERROR_REMOVING_ATTACHMENTS,
					componentType: MessageComponentType.FLASH_MESSAGE,
					message: `('${attachmentsWithError.map(a => a[isPost ? 'file_name' : 'uuid']).toString()}')`,
				});
			} else {
				onSuccess({
					type: MessageType.TYPE_SUCCESS,
					code: isPost ? InnerMessageAuthorityMessageCode.AUTHORITY_SUCCESS_ATTACHMENTS_ADDED : InnerMessageAuthorityMessageCode.AUTHORITY_SUCCESS_ATTACHMENTS_REMOVED,
					componentType: MessageComponentType.FLASH_MESSAGE
				});
			}
		} else {
			onFailure(MessageManagerUtilities.getMessageDetailsFromResponse(response));
		}
	}

}

interface AttachmentResponse {
	status: string;
	uuid?: string;
	file_name?: string;
	file_content?: string;
	description?: string;
}

export enum AttachmentRequestMethod {
	POST = 'POST',
	DELETE = 'DELETE'
}

enum AttachmentStatus {
	UPLOADED = 'UPLOADED',
	ERROR = 'ERROR',
	DELETED = 'DELETED',
	UNABLE_TO_DELETED = 'UNABLE_TO_DELETED'
}
